import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, ListState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Upload, Pencil, Trash2, Database, Download } from "lucide-react";
import { toast } from "sonner";
import { downloadBlob } from "@/components/roster/shiftColours";
import { INDIAN_STATES, STATE_NAME } from "@/lib/states";
import { Checkbox } from "@/components/ui/checkbox";

const MASTERS = [
  { key: "locations", label: "Locations", hasState: true },
  { key: "departments", label: "Departments", hasState: false },
  { key: "designations", label: "Designations", hasState: false },
  { key: "zones", label: "Zones", hasStates: true },
];
const EMPTY = { name: "", code: "", state_codes: [], state_code: "", address: "" };

function ZoneStates({ value, onChange, items, editingId }) {
  const owner = {};
  items.filter((z) => z.id !== editingId).forEach((z) => (z.state_codes || []).forEach((c) => { owner[c] = z.name; }));
  return (
    <div><Label>States in this zone ({value.length})</Label>
      <div className="mt-1.5 grid max-h-64 grid-cols-1 gap-1 overflow-y-auto rounded-md border border-slate-200 p-2 sm:grid-cols-2" data-testid="zone-states">
        {INDIAN_STATES.map((s) => (
          <label key={s.code} className={`flex items-center gap-2 rounded px-1.5 py-1 text-sm ${owner[s.code] ? "text-slate-400" : "cursor-pointer hover:bg-slate-50"}`}>
            <Checkbox checked={value.includes(s.code)} disabled={!!owner[s.code]} data-testid={`zone-state-${s.code}`}
              onCheckedChange={(v) => onChange(v ? [...value, s.code] : value.filter((c) => c !== s.code))} />
            {s.name}{owner[s.code] && <span className="text-xs">({owner[s.code]})</span>}
          </label>
        ))}
      </div>
      <p className="mt-1 text-xs text-slate-500">A state can belong to one zone only. Picking a state for an employee fills in its zone.</p>
    </div>
  );
}

export default function Masters() {
  const { actingOrgId } = useAuth();
  return (
    <div>
      <PageHeader title="Masters" description="Manage locations, departments, designations and zones" />
      <Tabs defaultValue="locations">
        <TabsList className="mb-5">
          {MASTERS.map((m) => <TabsTrigger key={m.key} value={m.key} data-testid={`master-tab-${m.key}`}>{m.label}</TabsTrigger>)}
        </TabsList>
        {MASTERS.map((m) => (
          <TabsContent key={m.key} value={m.key}>
            <MasterTable master={m} orgKey={actingOrgId} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

function MasterTable({ master, orgKey }) {
  const qc = useQueryClient();
  const fileRef = useRef();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const { data: items = [], isLoading, isError, error, refetch } = useQuery({
    queryKey: ["master", master.key, orgKey],
    queryFn: async () => (await api.get(`/org/masters/${master.key}`)).data,
  });

  const save = useMutation({
    mutationFn: async () => {
      if (editing) return (await api.put(`/org/masters/${master.key}/${editing.id}`, form)).data;
      return (await api.post(`/org/masters/${master.key}`, form)).data;
    },
    onSuccess: () => {
      toast.success(editing ? "Updated" : "Created");
      close();
      qc.invalidateQueries({ queryKey: ["master", master.key] });
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/org/masters/${master.key}/${id}`)).data,
    onSuccess: () => { toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["master", master.key] }); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const importM = useMutation({
    mutationFn: async (file) => {
      const fd = new FormData(); fd.append("file", file);
      return (await api.post(`/org/masters/${master.key}/bulk-import`, fd)).data;
    },
    onSuccess: (d) => { toast.success(`${d.created} added, ${d.skipped} skipped`); qc.invalidateQueries({ queryKey: ["master", master.key] }); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const close = () => { setOpen(false); setEditing(null); setForm(EMPTY); };
  const openEdit = (item) => { setEditing(item); setForm({ name: item.name, code: item.code || "", state_code: item.state_code || "", address: item.address || "", state_codes: item.state_codes || [] }); setOpen(true); };
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <Dialog open={open} onOpenChange={(v) => v ? setOpen(true) : close()}>
          <DialogTrigger asChild><Button data-testid={`add-${master.key}`}><Plus className="mr-2 h-4 w-4" /> Add {master.label.slice(0, -1).toLowerCase()}</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} {master.label.slice(0, -1)}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Name *</Label><Input value={form.name} onChange={upd("name")} className="mt-1.5" data-testid={`${master.key}-name`} /></div>
              <div><Label>Code</Label><Input value={form.code} onChange={upd("code")} className="mt-1.5" data-testid={`${master.key}-code`} /></div>
              {master.hasState && (
                <>
                  <div><Label>State code (e.g. WB)</Label><Input value={form.state_code} maxLength={2} onChange={upd("state_code")} className="mt-1.5 uppercase" data-testid={`${master.key}-state`} /></div>
                  <div><Label>Address</Label><Input value={form.address} onChange={upd("address")} className="mt-1.5" data-testid={`${master.key}-address`} /></div>
                </>
              )}
              {master.hasStates && <ZoneStates value={form.state_codes} onChange={(v) => setForm({ ...form, state_codes: v })} items={items} editingId={editing?.id} />}
            </div>
            <DialogFooter>
              <Button disabled={!form.name || save.isPending} onClick={() => save.mutate()} data-testid={`${master.key}-save`}>
                {save.isPending ? "Saving…" : "Save"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <input ref={fileRef} type="file" accept=".xlsx,.csv" className="hidden" data-testid={`${master.key}-import-input`}
          onChange={(e) => { if (e.target.files?.[0]) importM.mutate(e.target.files[0]); e.target.value = ""; }} />
        <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid={`import-${master.key}`}>
          <Upload className="mr-2 h-4 w-4" /> Bulk import
        </Button>
        <Button variant="ghost" onClick={() => downloadBlob(api, `/org/masters/${master.key}/template`, `${master.key}_template.xlsx`)} data-testid={`template-${master.key}`}>
          <Download className="mr-2 h-4 w-4" /> Download template
        </Button>
      </div>

      <ListState isLoading={isLoading} isError={isError} error={error} onRetry={refetch}
        isEmpty={items.length === 0} emptyTitle={`No ${master.label.toLowerCase()} yet`} emptyHint="Add one or bulk import" icon={Database}>
        <Card className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead><TableHead>Code</TableHead>
                {master.hasState && <><TableHead>State</TableHead><TableHead>Address</TableHead></>}
                {master.hasStates && <TableHead>States</TableHead>}
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((it) => (
                <TableRow key={it.id} data-testid={`${master.key}-row-${it.name}`}>
                  <TableCell className="text-sm font-medium">{it.name}</TableCell>
                  <TableCell className="text-sm">{it.code || "-"}</TableCell>
                  {master.hasState && <><TableCell className="text-sm">{it.state_code || "-"}</TableCell><TableCell className="text-sm">{it.address || "-"}</TableCell></>}
                  {master.hasStates && <TableCell className="max-w-md text-sm" data-testid={`zone-states-${it.name}`}>{(it.state_codes || []).map((c) => STATE_NAME[c] || c).join(", ") || "-"}</TableCell>}
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(it)} data-testid={`edit-${master.key}-${it.name}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => del.mutate(it.id)} data-testid={`delete-${master.key}-${it.name}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </ListState>
    </div>
  );
}
